<template>
  <div class="w-full">
    <div
      v-cloak
      ref="customization-share-popover"
      class="customization-share-popover"
      qaid="customization-share-popover"
    >
      <ul class="nav-list block-list text-left">
        <li class="block-list__item py-0">
          <pdl-button
            class="px-1 py-1 w-full justify-start radius-0"
            button
            text
            qaid="share-popover-email"
            icon="email"
            :disabled="isProcessingCustomizationAction"
            @click="initiateShareAction('EMAIL')"
          >
            <span>{{ $t('product.share.email') }}</span>
          </pdl-button>
        </li>

        <li class="block-list__item py-0">
          <pdl-button
            class="px-1 py-1 w-full justify-start radius-0"
            button
            text
            qaid="share-popover-download-print"
            icon="get_app"
            :disabled="isProcessingCustomizationAction"
            @click="initiateShareAction('DOWNLOAD')"
          >
            <span>{{ `${$t('text.download')} / ${$t('product.share.print')}` }}</span>
          </pdl-button>
        </li>

        <li class="block-list__item py-0">
          <pdl-button
            class="px-1 py-1 w-full justify-start radius-0"
            button
            text
            qaid="share-popover-copy-id"
            icon="content_copy"
            :disabled="isProcessingCustomizationAction"
            @click="initiateShareAction('COPY')"
          >
            <span>{{ $t('projectOne.summary.copyBuildLink') }}</span>
          </pdl-button>
        </li>
      </ul>
    </div>

    <pdl-button
      ref="tippy_shareTooltipId"
      v-tippy="tippyOptions"
      qaid="details-header-share-btn"
      icon-right
      icon="expand_more"
      secondary
      block
    >
      <span>{{ $t('product.share.share') }}</span>
    </pdl-button>
  </div>
</template>

<script>
import {mapState, mapMutations, mapActions, mapGetters} from 'vuex';
import {PdlButton} from '@pedal/pdl-button';
import {useCopyBuildUrl} from '@/familyDetailsPage/helpers/copyBuild.js';
import {useDownloadBuildPdf} from '@/familyDetailsPage/helpers/downloadPdf.js';
import {PdlToastType} from '@/constants/pdl-toast-type';
import {SUMMARY_DIALOG_VIEWS} from '@/familyDetailsPage/store/bike-customization';

const SHARE_ACTION = {
  EMAIL: 'EMAIL',
  DOWNLOAD: 'DOWNLOAD',
  COPY: 'COPY',
};

export default {
  name: 'CustomizationShareDropdown',
  components: {PdlButton},
  computed: {
    ...mapState('bikeCustomization', [
      'buildId',
      'queuedShareAction',
      'bikeData',
      'hasSavedBuildAndUpdatedRoute',
      'isCustomizationSummaryDialogOpen',
      'isSavedByUser',
      'isProcessingCustomizationAction',
    ]),
    ...mapState('backend', ['b2b', 'isUserLoggedIn', 'isConsumerFriendlyMode']),
    ...mapGetters('bikeCustomization', ['isSavedBuild']),

    tippyOptions() {
      return {
        allowHTML: true,
        content: this.getSharePopoverElement,
        interactive: true,
        trigger: 'click',
        placement: this.isCustomizationSummaryDialogOpen ? 'top' : 'bottom',
        flip: false,
        sticky: true,
        theme: 'flush',
      };
    },
  },
  watch: {
    hasSavedBuildAndUpdatedRoute(newState) {
      if (newState && this.queuedShareAction && this.isSavedByUser) {
        this.triggerShareAction(this.queuedShareAction);
      }
    },
  },
  methods: {
    ...mapMutations('bikeCustomization', [
      'setIsSaveDialogVisible',
      'setHasQueuedSaveAction',
      'setCachedBuildData',
      'setIsCustomizationShareEmailDialogOpen',
      'setQueuedShareAction',
      'clearQueuedShareAction',
      'setHasSavedBuildAndUpdatedRoute',
      'setSummaryDialogView',
      'setReopenPreviouslyOpenDialog',
      'setIsProcessingCustomizationAction',
    ]),
    ...mapActions('bikeCustomization', ['saveConfig']),

    async init() {
      this.initiateShareAction(this.queuedShareAction);
    },
    async sendEmail() {
      if (this.isCustomizationSummaryDialogOpen) {
        this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.SHARE_EMAIL);
      } else {
        this.setIsCustomizationShareEmailDialogOpen(true);
      }
    },
    async copyUrl() {
      const {copyUrlToClipboard} = useCopyBuildUrl();
      const hasCopiedUrl = await copyUrlToClipboard();

      if (hasCopiedUrl) {
        this.$notify({
          type: PdlToastType.SUCCESS,
          message: this.$t('projectOne.config.summary.linkCopied'),
        });
      }
      this.clearQueuedShareAction();
    },
    async downloadPdf() {
      this.setIsProcessingCustomizationAction(true);

      const {downloadBuildPdf} = useDownloadBuildPdf();
      const formData = {
        showHeader: false,
        showBikeImage: true,
        showSizingSelections: false,
        showPricing: true,
        pdfView: true,
        tidyHTML: true,
      };

      await downloadBuildPdf({
        buildId: this.buildId,
        formData,
      }).catch((error) => {
        console.error(error);
        this.$notify({
          type: PdlToastType.WARNING,
          message: this.$t('errorMessage.unableToProcess'),
        });
      });

      this.clearQueuedShareAction();
      return this.setIsProcessingCustomizationAction(false);
    },
    async initiateShareAction(action) {
      this.closeTooltip();

      if (!this.isUserLoggedIn) {
        this.setReopenPreviouslyOpenDialog(true);
        this.setIsProcessingCustomizationAction(true);
        this.setCachedBuildData();
        this.setQueuedShareAction(action);
        this.setHasQueuedSaveAction(true);
        await this.$nextTick();
        window.location.href = '/login/';
        return;
      }

      if (!this.isSavedBuild) {
        this.queueShareActionAndPromptSave(action);
        return;
      }

      if (this.isSavedBuild && !this.isSavedByUser) {
        this.setQueuedShareAction(action);
        this.checkIfBuildBelongsToUserBySaving();
        return;
      }

      if (this.isSavedByUser) {
        if (this.isCustomizationSummaryDialogOpen && action === SHARE_ACTION.EMAIL) {
          this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.SHARE_EMAIL);
        } else {
          return this.triggerShareAction(action);
        }
      }
    },
    queueShareActionAndPromptSave(action) {
      this.setQueuedShareAction(action);
      if (this.isCustomizationSummaryDialogOpen) {
        this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.SAVE_BUILD);
      } else {
        this.setIsSaveDialogVisible(true);
      }
    },
    async checkIfBuildBelongsToUserBySaving() {
      try {
        this.setIsProcessingCustomizationAction(true);
        await this.saveConfig();
      } catch (error) {
        console.error(error);
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('errorMessage.unableToProcess'),
        });
      } finally {
        this.setIsProcessingCustomizationAction(false);
      }
    },
    triggerShareAction(action) {
      if (!this.isSavedByUser || !this.isUserLoggedIn || !action?.length) {
        return;
      }

      switch (action) {
        case SHARE_ACTION.EMAIL:
          return this.sendEmail();
        case SHARE_ACTION.COPY:
          return this.copyUrl();
        case SHARE_ACTION.DOWNLOAD:
          return this.downloadPdf();
      }
    },
    getSharePopoverElement() {
      return this.$refs['customization-share-popover'];
    },
    closeTooltip() {
      let el = this.$refs['tippy_shareTooltipId'];

      if (el && el.$el._tippy) {
        el.$el._tippy.hide();
      }
    },
  },
};
</script>
<style scoped>
.customization-share-popover {
  z-index: 10000;
}
</style>
